import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { palletPath, paths } from '../../app/routes'
import { useToast } from '../../components/feedback/toastContext'
import { BACKSPACE_KEY, CLEAR_KEY, Keypad } from '../../components/ui/Keypad'
import { Button } from '../../components/ui/Button'
import { fromFaDigits, toFaDigits } from '../../lib/faNumbers'
import { resolveManualTarget } from './manualTarget'
import styles from './ManualScreen.module.css'

const MAX_DIGITS = 12

/**
 * The keypad is the fallback for a damaged or unreadable label, which is a real and
 * frequent case in a warehouse — it is not a secondary path.
 *
 * The keypad types digits only, as the artboard's does, so the code's prefix is supplied
 * here: «PLT-» for a pallet, «SN-» for a serial, mirroring the artboard's own
 * `'SN-' + manual`. The prefix is shown in the display so the operator sees the whole
 * code rather than guessing what it will become.
 */
export function ManualScreen() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { show } = useToast()
  const [digits, setDigits] = useState('')

  const target = resolveManualTarget(state)
  const prefix = target.kind === 'serial' ? 'SN-' : 'PLT-'

  const press = (key: string) => {
    if (key === CLEAR_KEY) {
      setDigits('')
      return
    }

    if (key === BACKSPACE_KEY) {
      setDigits((current) => current.slice(0, -1))
      return
    }

    setDigits((current) => (current + key).slice(0, MAX_DIGITS))
  }

  const submit = () => {
    if (digits === '') {
      show('کدی وارد نشده است', 'warn')
      return
    }

    // The keypad shows Persian digits; the code that leaves the device is Latin.
    const code = `${prefix}${fromFaDigits(digits)}`

    if (target.kind === 'pallet') {
      navigate(palletPath(code), { replace: true })
      return
    }

    if (target.kind === 'assignPallet') {
      navigate(paths.assign, { replace: true, state: { manualPallet: code } })
      return
    }

    navigate(palletPath(target.palletCode ?? ''), {
      replace: true,
      state: { manualSerial: code },
    })
  }

  return (
    <div className={styles.screen}>
      <div className={styles.hint}>برای بارکد آسیب‌دیده یا ناخوانا، کد را دستی وارد کنید.</div>

      <div className={styles.display} aria-live="polite">
        <span className={styles.prefix}>{prefix}</span>
        {digits === '' ? <span className={styles.placeholder}>—</span> : toFaDigits(digits)}
      </div>

      <Keypad onPress={press} />

      <Button onClick={submit}>ثبت کد</Button>
    </div>
  )
}
